import { JSONRequest } from "@certn/api";
import { useEffect, useState } from "react";

type ClickwrapContract = {
  contract_data: {
    [key: string]: {
      title: string;
      download_url: string;
      published_version: string;
    };
  };
  versions: string[];
  contract_html: string;
};

type ClickwrapConfig = {
  accessID: string;
  contractIDs: string[];
  signerID?: string;
  renderData?: Record<string, unknown>;
  testMode?: boolean;
  style?: "scroll" | "full";
};

type ClickwrapEvent = "agreed" | "disagreed" | "visited" | "sent" | "updated";

export function useContract({
  accessID,
  contractIDs,
  signerID,
  renderData,
  style = "scroll",
  testMode = true,
}: ClickwrapConfig) {
  const [data, setData] = useState<ClickwrapContract>();
  const [agreedContracts, setAgreedContracts] = useState<
    Record<string, boolean>
  >({});
  const [isError, setIsError] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>();

  useEffect(() => {
    let isCancelled = false;
    setData(undefined);

    const fetchContracts = async () => {
      try {
        setIsLoading(true);
        // https://clickwrap-developer.ironcladapp.com/reference/retrieve-group-json
        const response = await JSONRequest<ClickwrapContract>(
          "https://pactsafe.io/load/json",
          {
            method: "POST",
            body: JSON.stringify({
              sid: accessID,
              filter: `id==${contractIDs.join(",")}`,
              style,
              rnd: renderData,
              dyn: !!renderData,
            }),
          }
        );

        if (!isCancelled) setData(response);
      } catch (e) {
        console.error("Unable to fetch contracts:", e);
        if (!isCancelled) setIsError(true);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    const fetchAgreed = async () => {
      try {
        // https://clickwrap-developer.ironcladapp.com/reference/get-the-latest-versions-signed
        const response = await JSONRequest<Record<string, boolean>>(
          `https://pactsafe.io/latest?sid=${accessID}&sig=${signerID}&cid=${contractIDs.join(
            ","
          )}&tm=${testMode}`
        );

        if (!isCancelled) setAgreedContracts(response);
      } catch (e) {
        console.error("Unable to fetch latest signed contracts:", e);
      }
    };

    fetchContracts();
    signerID && fetchAgreed();

    return () => {
      isCancelled = true;
    };
  }, [
    signerID,
    contractIDs.length,
    accessID,
    contractIDs,
    renderData,
    testMode,
    style,
  ]);

  const getContractHTML = (contractID?: string) => {
    // take the html component and remove anything we don't use,
    // keeping only document content and some styles
    const htmlObject = document.createElement("div");
    htmlObject.innerHTML = data?.contract_html ?? "";
    const fragment = document.createDocumentFragment();
    fragment.appendChild(htmlObject);

    Array.from(htmlObject.getElementsByClassName("ps-contract")).forEach(
      (element) => {
        // if specified, remove html of contracts that don't match given ID
        if (contractID && element.id !== `ps-contract-${contractID}`) {
          element.remove();
          return;
        }

        // removes style="display: none;"
        element.removeAttribute("style");
      }
    );
    Array.from(htmlObject.getElementsByTagName("label")).forEach((element) =>
      element.remove()
    );
    Array.from(
      htmlObject.getElementsByClassName("ps-contract-options")
    ).forEach((element) => element.remove());
    Array.from(htmlObject.getElementsByClassName("ps-contract-title")).forEach(
      (element) => element.remove()
    );

    return htmlObject.outerHTML;
  };

  const triggerEvent = (eventType: ClickwrapEvent) => (versions: string[]) =>
    send({
      accessID,
      signerID,
      versions,
      eventType,
      testMode,
    });

  return {
    data,
    isAgreed: (contractID: string) => agreedContracts[contractID] || false,
    getContractHTML,
    isError,
    isLoading,
    agree: triggerEvent("agreed"),
    disagree: triggerEvent("disagreed"),
    visit: triggerEvent("visited"),
  };
}

async function send({
  accessID,
  signerID,
  versions,
  eventType,
  renderData = {},
  testMode = true,
}: {
  accessID: string;
  signerID?: string;
  versions: string[];
  eventType: "agreed" | "disagreed" | "visited" | "sent" | "updated";
  renderData?: Record<string, unknown>;
  testMode?: boolean;
}) {
  try {
    // https://clickwrap-developer.ironcladapp.com/reference/send-contracts-signedaccepted-by-signer
    await JSONRequest(
      `https://pactsafe.io/send?sid=${accessID}&sig=${signerID}&vid=${versions.join(
        ","
      )}&tm=${testMode}&et=${eventType}&rnd=${JSON.stringify(renderData)}`,
      {
        method: "POST",
        body: JSON.stringify({ isTextResponse: true }),
      }
    );
  } catch (e) {
    console.error(`Unable to send "${eventType}" event:`, e);
    throw new Error("Failed to send");
  }
}
